import { Component, Input, ChangeDetectionStrategy, ChangeDetectorRef, EventEmitter, Output, ViewChild, ElementRef, AfterViewInit, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { WidgetEdit } from '../../../../shared/components/widget-edit/widget-edit';
import { DealsOffersService, Brand } from '../../../../core/services/deals-offers.service';
import { switchMap, map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { VoucherPurchaseModalComponent } from './voucher-purchase-modal/voucher-purchase-modal';

interface DealOffer {
    id: number;
    category: string; // Primary category for display badge
    allCategories: string[]; // All categories this brand belongs to
    title: string;
    image: string;
    countdown: string; // Format: "DD : HH : MM : SS"
    categoryColor: string;
    categoryBgColor: string;
    originalBrand?: Brand;
}

@Component({
    selector: 'app-deals-offers-widget',
    standalone: true,
    imports: [CommonModule, WidgetEdit, CdkOverlayOrigin, VoucherPurchaseModalComponent],
    templateUrl: './deals-offers.html',
    styleUrls: ['./deals-offers.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DealsOffers implements OnInit {
    @Input() title = 'Deals and offers';
    @Input() size: 'small' | 'medium' | 'half' | 'full' = 'medium';
    @Input() index = 0;
    @Input() isEditMode = false;
    @Input() isAddMode: boolean = false;
    @Output() addWidget = new EventEmitter<void>();
    @Input() activeDropdownIndex: number | null = null;
    @Input() getAvailableSizes!: (index: number) => Array<{ value: string; label: string }>;
    @Input() toggleSizeDropdown!: (index: number, event: Event) => void;
    @Input() changeWidgetSize!: (index: number, size: any) => void;
    @Input() removeWidget!: (index: number) => void;

    categories: string[] = ['All'];
    selectedCategory: string = 'All';
    currentOfferIndex = 0;

    @ViewChild('offersContainer') offersContainer!: ElementRef;
    cardWidth = 0;
    cardHeight = 0;
    private resizeObserver: ResizeObserver | null = null;

    showVoucherModal: boolean = false;
    selectedVoucherBrand: Brand | null = null;
    userMobile: string = '';

    @Input() offers: DealOffer[] = [];

    // Helper for mapping categories to styles/images
    private categoryConfig: { [key: string]: { color: string, bgColor: string, defaultImage: string } } = {
        'Travel': { color: 'var(--color-orange)', bgColor: 'var(--bg-orange-opacity)', defaultImage: 'assets/images/hot-offer-discount-sea.jpg' },
        'Food': { color: 'var(--color-orange)', bgColor: 'var(--bg-orange-opacity)', defaultImage: 'assets/images/promocode-for-coefee.jpeg' },
        'Hotels': { color: 'var(--color-green)', bgColor: 'var(--bg-green-opacity)', defaultImage: 'assets/images/photo-hotels.jpeg' },
        'Bank Offers': { color: 'var(--color-bank-blue)', bgColor: 'var(--bg-bank-blue-opacity)', defaultImage: 'assets/images/bankoffers.jpeg' },
        'Bus': { color: 'var(--color-red)', bgColor: 'var(--bg-red-opacity)', defaultImage: 'assets/images/hot-offer-discount-sea.jpg' },
        'Electronic': { color: 'var(--color-purple)', bgColor: 'var(--bg-purple-opacity)', defaultImage: 'assets/images/bankoffers.jpeg' }, // Placeholder image
        'Lifestyle': { color: 'var(--color-yellow)', bgColor: 'var(--bg-yellow-opacity)', defaultImage: 'assets/images/promocode-for-coefee.jpeg' } // Placeholder image
    };

    constructor(
        private cdr: ChangeDetectorRef,
        private fiService: DealsOffersService
    ) { }

    ngOnInit() {
        this.userMobile = localStorage.getItem('consent_mobile_number') || '';
        this.fetchDeals();
    }

    fetchDeals() {
        this.fiService.getDealsWithAuth().pipe(
            map(brands => {
                if (brands && brands.length > 0) {
                    return brands
                        .filter(brand => brand.BrandName && brand.BrandName.trim() !== '')
                        //.filter(brand => !this.isTestBrand(brand))
                        .map((brand, index) => this.mapBrandToOffer(brand, index + 1));
                }
                return [];
            }),
            catchError(error => {
                return of([]);
            })
        ).subscribe(offers => {
            if (offers.length > 0) {
                this.offers = offers;
                this.updateAvailableCategories(offers);
                this.cdr.markForCheck();

                // Re-measure card dimensions after DOM renders with new offers
                setTimeout(() => {
                    this.updateMeasurements();
                    this.cdr.detectChanges();
                }, 100);
            }
        });
    }

    private isTestBrand(brand: Brand): boolean {
        const testKeywords = ['test', 'demo', 'sample', 'yepme'];
        const name = brand.BrandName.toLowerCase();
        const category = brand.Category ? brand.Category.toLowerCase() : '';
        return testKeywords.some(keyword => name.includes(keyword) || category.includes(keyword));
    }

    private mapBrandToOffer(brand: Brand, id: number): DealOffer {
        const allCategories = this.determineCategories(brand);
        const primaryCategory = allCategories[0]; // First category for display badge
        const config = this.getCategoryStyle(primaryCategory);

        // Countdown simulation (random future date)
        const countdown = this.generateRandomCountdown();

        return {
            id: id,
            category: primaryCategory,
            allCategories: allCategories,
            title: `${brand.BrandName} - ${brand.Brandtype === 'PROMOCODE' ? 'Get Discount Code' : 'Special Offer'}`,
            image: brand.BrandImage || config.defaultImage,
            countdown: countdown,
            categoryColor: config.color,
            categoryBgColor: config.bgColor,
            originalBrand: brand
        };
    }

    /**
     * Returns ALL categories for a brand (parsed from comma-separated Category field).
     * Each brand can belong to multiple categories.
     */
    private determineCategories(brand: Brand): string[] {
        const name = brand.BrandName.toLowerCase();

        // Specific brand overrides
        if (name.includes('myntra')) return ['Lifestyle'];

        if (brand.Category) {
            // Split categories by comma and return ALL unique categories
            const parts = brand.Category.split(',')
                .map(p => p.trim())
                .filter(p => p.length > 0)
                .map(p => this.toTitleCase(p));

            if (parts.length > 0) {
                // Remove duplicates
                return [...new Set(parts)];
            }
        }

        // Fallback logic if Category field is missing/empty
        if (name.includes('hotel')) return ['Hotels'];
        if (name.includes('bank')) return ['Bank Offers'];

        return ['Lifestyle']; // Final default
    }

    private toTitleCase(str: string): string {
        return str.replace(
            /\w\S*/g,
            text => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase()
        );
    }

    private getCategoryStyle(category: string): { color: string, bgColor: string, defaultImage: string } {
        // Try exact match
        if (this.categoryConfig[category]) {
            return this.categoryConfig[category];
        }

        // Try case-insensitive match
        const lowerCat = category.toLowerCase();
        const configKey = Object.keys(this.categoryConfig).find(k => k.toLowerCase() === lowerCat);
        if (configKey) {
            return this.categoryConfig[configKey];
        }

        // Partial match mappings
        if (lowerCat.includes('travel') || lowerCat.includes('flight') || lowerCat.includes('holiday')) return this.categoryConfig['Travel'];
        if (lowerCat.includes('food') || lowerCat.includes('restaurant') || lowerCat.includes('dining')) return this.categoryConfig['Food'];
        if (lowerCat.includes('bus')) return this.categoryConfig['Bus'];
        if (lowerCat.includes('hotel')) return this.categoryConfig['Hotels'];
        if (lowerCat.includes('electronic') || lowerCat.includes('gadget')) return this.categoryConfig['Electronic'];
        if (lowerCat.includes('bank') || lowerCat.includes('card') || lowerCat.includes('financial')) return this.categoryConfig['Bank Offers'];

        // Fallback to Lifestyle or cycle through colors based on string hash if needed
        // For now, default to Lifestyle style
        return this.categoryConfig['Lifestyle'];
    }

    private generateRandomCountdown(): string {
        // Just a placeholder generator to match the UI format "DD : HH : MM : SS"
        const d = Math.floor(Math.random() * 10) + 1;
        const h = Math.floor(Math.random() * 23);
        const m = Math.floor(Math.random() * 59);
        const s = Math.floor(Math.random() * 59);
        return `${d.toString().padStart(2, '0')} : ${h.toString().padStart(2, '0')} : ${m.toString().padStart(2, '0')} : ${s.toString().padStart(2, '0')}`;
    }

    openVoucherModal(offer: DealOffer): void {
        if (this.isEditMode || this.isAddMode) return;

        if (offer.originalBrand) {
            this.selectedVoucherBrand = offer.originalBrand;
            this.showVoucherModal = true;
            this.cdr.markForCheck();
        } else {
            console.warn('No brand details available for this offer');
        }
    }

    closeVoucherModal(): void {
        this.showVoucherModal = false;
        this.selectedVoucherBrand = null;
        this.cdr.markForCheck();
    }

    ngAfterViewInit() {
        this.updateMeasurements();
        if (this.offersContainer) {
            this.resizeObserver = new ResizeObserver(() => {
                window.requestAnimationFrame(() => {
                    if (!this.resizeObserver) return;
                    this.updateMeasurements();
                    this.cdr.detectChanges();
                });
            });
            this.resizeObserver.observe(this.offersContainer.nativeElement);
        }
    }

    ngOnDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    private updateMeasurements() {
        if (this.offersContainer) {
            const card = this.offersContainer.nativeElement.querySelector('.deals-offers-widget__offer-card');
            if (card) {
                const style = window.getComputedStyle(card);
                const marginTop = parseFloat(style.marginTop) || 0;
                const marginBottom = parseFloat(style.marginBottom) || 0;
                const marginLeft = parseFloat(style.marginLeft) || 0;
                const marginRight = parseFloat(style.marginRight) || 0;

                // Track uses gap: 8px in CSS maybe? Let's check or just use offsetHeight + gap
                // Actually, let's just measure the distance between two cards if multiple exist
                const cards = this.offersContainer.nativeElement.querySelectorAll('.deals-offers-widget__offer-card');
                if (cards.length > 1) {
                    const rect1 = cards[0].getBoundingClientRect();
                    const rect2 = cards[1].getBoundingClientRect();
                    this.cardHeight = rect2.top - rect1.top;
                    this.cardWidth = rect2.left - rect1.left;
                } else {
                    this.cardHeight = card.offsetHeight + 8; // Fallback to 8px gap
                    this.cardWidth = card.offsetWidth + 8;
                }
            }
        }
    }

    get allCategoryOffers(): DealOffer[] {
        if (this.selectedCategory === 'All') {
            return this.offers;
        }
        // Show brand if ANY of its categories match the selected category
        return this.offers.filter(offer => offer.allCategories.includes(this.selectedCategory));
    }

    get filteredOffers(): DealOffer[] {
        return this.allCategoryOffers;
    }

    selectCategory(category: string, event?: MouseEvent): void {
        if (this.isAddMode) return;
        this.selectedCategory = category;
        this.currentOfferIndex = 0; // Reset slider when category changes

        // Auto-scroll to bring the clicked category into view
        if (event) {
            const clickedButton = event.target as HTMLElement;
            const categoryContainer = document.querySelector('.deals-offers-widget__categories');

            if (categoryContainer && clickedButton) {
                const button = clickedButton.closest('.deals-offers-widget__category-btn') as HTMLElement;

                if (button) {
                    // Get button position relative to container
                    const containerRect = categoryContainer.getBoundingClientRect();
                    const buttonRect = button.getBoundingClientRect();

                    // Calculate if button is outside visible area
                    const isLeftOutside = buttonRect.left < containerRect.left;
                    const isRightOutside = buttonRect.right > containerRect.right;

                    if (isLeftOutside || isRightOutside) {
                        // Scroll to center the clicked button
                        const scrollLeft = button.offsetLeft - (categoryContainer.clientWidth / 2) + (button.clientWidth / 2);
                        categoryContainer.scrollTo({
                            left: scrollLeft,
                            behavior: 'smooth'
                        });
                    }
                }
            }
        }

        this.cdr.markForCheck();

        // Re-measure card dimensions after DOM updates with new filtered offers
        setTimeout(() => {
            this.updateMeasurements();
            this.cdr.detectChanges();
        }, 50);
    }

    scrollCategories(direction: 'left' | 'right'): void {
        if (this.isAddMode) return;
        const container = document.querySelector('.deals-offers-widget__categories');
        if (container) {
            const scrollAmount = 120;
            container.scrollBy({
                left: direction === 'right' ? scrollAmount : -scrollAmount,
                behavior: 'smooth'
            });
        }
    }

    // Offer slider navigation
    previousOffer(): void {
        if (this.isAddMode) return;
        if (this.currentOfferIndex > 0) {
            this.currentOfferIndex--;
            this.cdr.markForCheck();
        }
    }

    nextOffer(): void {
        if (this.isAddMode) return;
        const maxIndex = this.allCategoryOffers.length - (this.size === 'small' ? 1 : 2);
        if (this.currentOfferIndex < maxIndex) {
            this.currentOfferIndex++;
            this.cdr.markForCheck();
        }
    }

    canGoPreviousOffer(): boolean {
        return this.currentOfferIndex > 0;
    }

    canGoNextOffer(): boolean {
        const maxIndex = this.allCategoryOffers.length - (this.size === 'small' ? 1 : 2);
        return this.currentOfferIndex < maxIndex && this.allCategoryOffers.length > (this.size === 'small' ? 1 : 2);
    }


    private updateAvailableCategories(offers: DealOffer[]): void {
        // Collect ALL unique categories from all offers' allCategories arrays
        const uniqueCategories = new Set<string>();
        offers.forEach(o => o.allCategories.forEach(cat => uniqueCategories.add(cat)));

        // Start with 'All'
        const dynamicCategories = ['All'];

        // Add other categories sorted alphabetically
        const sortedCats = Array.from(uniqueCategories).sort();
        dynamicCategories.push(...sortedCats);

        this.categories = dynamicCategories;

        // If selected category is no longer available, reset to 'All'
        if (!this.categories.includes(this.selectedCategory)) {
            this.selectedCategory = 'All';
            this.currentOfferIndex = 0;
        }
    }

    getSliderTransform(): string {
        if (this.size === 'small') {
            // Horizontal scroll for small size
            return `translateX(-${this.currentOfferIndex * this.cardWidth}px)`;
            // Horizontal scroll for small size
            return `translateX(-${this.currentOfferIndex * this.cardWidth}px)`;
        } else {
            // Vertical scroll for other sizes
            return `translateY(-${this.currentOfferIndex * this.cardHeight}px)`;
            return `translateY(-${this.currentOfferIndex * this.cardHeight}px)`;
        }
    }
}
